/* ─────────────────────────────────────────────────────────────────────────
   KIMDYMA · eb-components.js — the moving components. v4.0

     [data-eb-panels]     the horizontal accordion
     [data-eb-quotes]     the peek accordion that advances on its own
     [data-eb-rail]       the rail frame — progress graduation, arrows, count
     [data-eb-accordion]  vertical disclosure
     [data-eb-contact]    the contact form's one-topic narrowing
     [data-eb-switcher]   one .eb-switcher, N panels
     [data-eb-picker]     the theme's own select — one open at a time

   All delegated, all guarded against a double load, all additive: with this
   file removed every panel is a photograph with a label, every quote is
   readable, every rail scrolls with a finger, and every accordion is open.

   HOVER IS A POINTER THING. Panels open on hover only where (hover:hover) and
   (pointer:fine) both hold — on a touch screen a hover that opens something is
   a tap that opens the wrong thing. Click works everywhere; focus opens, so
   the keyboard sees what the mouse sees.
   ───────────────────────────────────────────────────────────────────────── */
(function () {
  'use strict';
  if (window.ebComponentsLoaded) return;
  window.ebComponentsLoaded = true;

  var root = document.documentElement;
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  var fine = window.matchMedia('(hover:hover) and (pointer:fine)');
  var slice = function (l) { return Array.prototype.slice.call(l); };
  function motionOff() { return reduce.matches || root.getAttribute('data-motion') === 'off'; }

  /* ══ PANELS · horizontal accordion ═════════════════════════════════════
     State lives on the item as data-open. The SPINE is the button and carries
     aria-expanded; when the panel is closed the spine is the entire visible
     tile, so the target is the tile. One open at a time, never none. */
  function panels(box) {
    var items = slice(box.querySelectorAll('.eb-panels__item'));
    if (!items.length) return;

    function open(item) {
      items.forEach(function (it) {
        var on = it === item;
        it.setAttribute('data-open', on ? 'true' : 'false');
        var spine = it.querySelector('.eb-panels__spine');
        if (spine) spine.setAttribute('aria-expanded', on ? 'true' : 'false');
      });
    }
    if (!items.some(function (it) { return it.getAttribute('data-open') === 'true'; })) open(items[0]);

    box.addEventListener('click', function (e) {
      var item = e.target.closest('.eb-panels__item');
      if (!item || !box.contains(item)) return;
      // A control inside an open panel (the play button, a link) is its own
      // click. Anywhere else on the tile opens it.
      if (item.getAttribute('data-open') === 'true' && e.target.closest('a,button:not(.eb-panels__spine)')) return;
      open(item);
    });
    box.addEventListener('pointerenter', function (e) {
      if (!fine.matches) return;
      var item = e.target.closest && e.target.closest('.eb-panels__item');
      if (item) open(item);
    }, true);
    box.addEventListener('focusin', function (e) {
      var item = e.target.closest('.eb-panels__item');
      if (item) open(item);
    });
    box.addEventListener('keydown', function (e) {
      var spine = e.target.closest('.eb-panels__spine');
      if (!spine) return;
      var dir = (e.key === 'ArrowRight' || e.key === 'ArrowDown') ? 1
              : (e.key === 'ArrowLeft' || e.key === 'ArrowUp') ? -1 : 0;
      if (!dir) return;
      e.preventDefault();
      var i = items.indexOf(spine.closest('.eb-panels__item'));
      var next = items[(i + dir + items.length) % items.length];
      var s = next.querySelector('.eb-panels__spine');
      if (s) s.focus(); else open(next);
    });
  }

  /* ══ QUOTES · the peek accordion ═══════════════════════════════════════
     Every review stays on the page; the open one carries its text, the rest
     fade back to their byline. The tick bar is the timer: a CSS animation
     over --play, and the change happens on animationend so bar and change can
     never drift. Hover or focus pauses it. Picking one stops it for good — a
     reader who chose is not to be moved along. Under reduced motion nothing
     advances and the bars are plain buttons. */
  function quotes(box) {
    var slides = slice(box.querySelectorAll('.eb-quotes__slide'));
    var ticks = slice(box.querySelectorAll('.eb-quotes__tick'));
    var count = box.querySelector('.eb-quotes__count');
    if (!slides.length) return;
    var current = Math.max(0, slides.findIndex(function (s) { return s.classList.contains('is-on'); }));

    function show(i) {
      current = (i + slides.length) % slides.length;
      slides.forEach(function (s, n) { s.classList.toggle('is-on', n === current); });
      ticks.forEach(function (t, n) {
        t.classList.toggle('is-on', n === current);
        t.classList.toggle('is-done', n < current);
        t.setAttribute('aria-pressed', n === current ? 'true' : 'false');
        var fill = t.querySelector('i');
        // Restart the bar from zero: remove and re-add the animation.
        if (fill) { fill.style.animation = 'none'; void fill.offsetWidth; fill.style.animation = ''; }
      });
      if (count) count.textContent = (current + 1) + ' / ' + slides.length;
    }
    show(current);

    box.addEventListener('animationend', function (e) {
      if (!e.target.closest('.eb-quotes__tick.is-on')) return;
      if (motionOff() || box.classList.contains('is-manual')) return;
      show(current + 1);
    });
    box.addEventListener('click', function (e) {
      var tick = e.target.closest('.eb-quotes__tick');
      var slide = e.target.closest('.eb-quotes__slide');
      var i = tick ? ticks.indexOf(tick) : slide ? slides.indexOf(slide) : -1;
      if (i < 0) return;
      box.classList.add('is-manual');
      show(i);
    });
    box.addEventListener('pointerenter', function () { box.classList.add('is-paused'); });
    box.addEventListener('pointerleave', function () { box.classList.remove('is-paused'); });
    box.addEventListener('focusin', function () { box.classList.add('is-paused'); });
    box.addEventListener('focusout', function (e) {
      if (!box.contains(e.relatedTarget)) box.classList.remove('is-paused');
    });
  }

  /* ══ RAIL FRAME ═════════════════════════════════════════════════════════
     The track scrolls itself. This writes two numbers onto the frame — how far
     along, how much is visible — and the CSS draws the graduation from them.
     The arrows move by most of a viewport, never a whole one, so the last card
     seen is the first card seen after the move: a page turn that loses its
     edge loses the reader's place. */
  function rail(frame) {
    var track = frame.querySelector('.eb-rail');
    if (!track) return;
    var prev = frame.querySelector('[data-eb-rail-prev]');
    var next = frame.querySelector('[data-eb-rail-next]');
    var count = frame.querySelector('[data-eb-rail-count]');
    var raf = null;

    function paint() {
      raf = null;
      var max = track.scrollWidth - track.clientWidth;
      frame.classList.toggle('is-static', max <= 1);
      if (max <= 1) return;
      var p = track.scrollLeft / max;
      frame.style.setProperty('--rail-p', p.toFixed(4));
      frame.style.setProperty('--rail-w', ((track.clientWidth / track.scrollWidth) * 100).toFixed(2) + '%');
      if (prev) prev.disabled = track.scrollLeft <= 1;
      if (next) next.disabled = track.scrollLeft >= max - 1;
      if (count) {
        var kids = slice(track.children), first = 0;
        for (var i = 0; i < kids.length; i++) {
          if (kids[i].offsetLeft - track.offsetLeft >= track.scrollLeft - 4) { first = i; break; }
        }
        count.textContent = (first + 1) + ' / ' + kids.length;
      }
    }
    function schedule() { if (!raf) raf = requestAnimationFrame(paint); }
    function go(dir) {
      track.scrollBy({ left: dir * track.clientWidth * 0.8, behavior: motionOff() ? 'auto' : 'smooth' });
    }
    track.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    if (prev) prev.addEventListener('click', function () { go(-1); });
    if (next) next.addEventListener('click', function () { go(1); });
    paint();
  }

  /* ══ ACCORDION · vertical disclosure ═══════════════════════════════════
     .is-open on the item; aria-expanded on the trigger; the panel's 0fr→1fr
     is in CSS. data-single on the container closes the others. */
  function accordion(box) {
    box.addEventListener('click', function (e) {
      var trigger = e.target.closest('.eb-accordion__trigger');
      if (!trigger || !box.contains(trigger)) return;
      var item = trigger.closest('.eb-accordion__item');
      var opening = !item.classList.contains('is-open');
      if (opening && box.hasAttribute('data-single')) {
        slice(box.querySelectorAll('.eb-accordion__item.is-open')).forEach(function (it) {
          it.classList.remove('is-open');
          var t = it.querySelector('.eb-accordion__trigger');
          if (t) t.setAttribute('aria-expanded', 'false');
        });
      }
      item.classList.toggle('is-open', opening);
      trigger.setAttribute('aria-expanded', opening ? 'true' : 'false');
    });
  }

  /* ══ VIDEO FACADE ══════════════════════════════════════════════════════
     Nothing is fetched until the visitor presses play. The player sits in a
     <template> until this moment — parked there rather than hidden with CSS,
     because a hidden iframe still loads, still runs and still sets whatever
     it likes. That is the entire reason the facade exists, and it is also why
     the page can promise no cookie is set before play. */
  function facade(box) {
    box.addEventListener('click', function (e) {
      if (!e.target.closest('[data-eb-facade-play]')) return;
      var tpl = box.querySelector('[data-eb-facade-player]');
      if (!tpl) return;
      var frag = tpl.content.cloneNode(true);
      slice(box.querySelectorAll('img, .eb-play, .eb-facade')).forEach(function (el) { el.remove(); });
      box.appendChild(frag);
      box.classList.add('is-playing');
      var media = box.querySelector('video');
      if (media && media.play) media.play().catch(function () {});
    });
  }

  /* ══ CONTACT · one topic rules the form ════════════════════════════════
     The topic select decides which extra field is asked for and what the
     promise beside the send button says. Everything here NARROWS a form that
     is already complete: the markup ships every field visible and optional,
     so a page whose script never arrived is a page you can still write from.

     data-topic on a router button is the same string as a topic option's
     value. That string is the whole contract between the two sections. */
  function contactForm(box) {
    var topic = box.querySelector('[data-eb-contact-topic]');
    if (!topic) return;
    var promise = box.querySelector('[data-eb-contact-promise]');
    var extras = slice(box.querySelectorAll('[data-when]'));

    /* The topic is the theme's picker, not a native select: a native select's
       open list is drawn by the operating system and cannot be made to look
       like anything else on the site. So the value lives in radios, which is
       also what lets the field submit with no script at all. */
    function value() {
      var on = topic.querySelector('input[type="radio"]:checked');
      return on ? on.value : '';
    }

    function sync() {
      var v = value();
      extras.forEach(function (el) {
        var on = el.getAttribute('data-when') === v;
        el.hidden = !on;
        /* A hidden field that still posts is a field the merchant has to
           read past in the admin. Disabling it keeps it out of the body. */
        slice(el.querySelectorAll('input, select, textarea')).forEach(function (f) {
          f.disabled = !on;
        });
      });
      if (promise) {
        /* data-promise-fit is dataset.promiseFit, and a topic value is the
           merchant's string — so it is read by attribute name, not dataset. */
        promise.textContent = promise.getAttribute('data-promise-' + v) ||
                              promise.getAttribute('data-promise-default') || '';
      }
    }

    topic.addEventListener('change', sync);
    sync();

    /* The router lives in another section, so the handover is delegated from
       the document rather than bound to buttons this box can see. */
    if (!document.ebContactRouted) {
      document.ebContactRouted = true;
      document.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-topic]');
        if (!btn) return;
        var form = document.querySelector('[data-eb-contact]');
        var sel = form && form.querySelector('[data-eb-contact-topic]');
        if (!form || !sel) return;            /* no form on the page: let the link work */
        var want = btn.getAttribute('data-topic');
        var radio = slice(sel.querySelectorAll('input[type="radio"]')).filter(function (r) {
          return r.value === want;
        })[0];
        if (!radio) return;                   /* topic the form does not offer: same */
        e.preventDefault();
        radio.checked = true;
        /* Bubbles, so the shell's picker controller writes the label onto the
           trigger and closes the panel on the way past. */
        radio.dispatchEvent(new Event('change', { bubbles: true }));
        form.scrollIntoView({ behavior: motionOff() ? 'auto' : 'smooth', block: 'start' });
        var first = form.querySelector('input:not([disabled]):not([type=hidden]):not([type=radio])');
        if (first) setTimeout(function () { first.focus({ preventScroll: true }); }, motionOff() ? 0 : 420);
      });
    }
  }

  /* ══ SWITCHER · one control, N panels ══════════════════════════════════
     .eb-switcher has shipped since v4.0 with no controller, so every section
     that wanted one wrote its own. This is that controller, once: the buttons
     carry data-switch, the panels carry data-switch-panel, and the values
     match. Nothing here knows what is inside a panel.

     It is an ENHANCEMENT. With the script gone the first panel is the one
     without `hidden` and the others are simply not shown — the section is
     still a section, showing one bike's figures instead of three. Markup that
     needs all of them visible without script should not use a switcher. */
  /* THE STRIP IS BUILT FROM THE PANELS, where a section cannot read its own
     theme blocks to write it. Each panel ships a hidden .eb-apply__tabsrc
     holding the icon and the label — drawn by the real icon snippet, because
     the icons are inlined SVG and not a sprite a script could reference — and
     they are lifted into one .eb-switcher at the top.

     The point is that the strip and the hiding below are the SAME pass: a
     panel can never be hidden with no tab to bring it back, which is exactly
     what happened when the section wrote the strip and came up empty. */
  function buildTabs(box) {
    if (box.querySelector('.eb-switcher')) return;
    var srcs = slice(box.querySelectorAll('.eb-apply__tabsrc'));
    if (srcs.length < 2) return;
    var strip = document.createElement('div');
    strip.className = 'eb-switcher';
    strip.setAttribute('role', 'tablist');
    if (box.getAttribute('data-switch-label')) {
      strip.setAttribute('aria-label', box.getAttribute('data-switch-label'));
    }
    srcs.forEach(function (src) {
      var b = document.createElement('button');
      b.type = 'button';
      b.setAttribute('role', 'tab');
      b.setAttribute('data-switch', src.getAttribute('data-switch'));
      /* Serialising DOM this document already parsed — not merchant text
         being re-parsed as markup. */
      b.innerHTML = src.innerHTML;
      strip.appendChild(b);
      src.parentNode.removeChild(src);
    });
    box.insertBefore(strip, box.firstChild);
  }

  /* A LINK THAT OPENS THE RIGHT TAB. "Apply as a bike shop" further up the
     page should land on the shop form, not on whichever tab was last used.
     The anchor still does the scrolling — this only presses the tab first,
     so with the script gone it is a link to the right section and the wrong
     tab, which is a page you can still finish on.

     Delegated on the document once, because the link and the switcher are
     two different sections and neither owns the other. */
  function bindSwitchLinks() {
    if (document.documentElement.dataset.ebSwitchLinks) return;
    document.documentElement.dataset.ebSwitchLinks = '1';
    document.addEventListener('click', function (e) {
      var a = e.target.closest('[data-switch-to]');
      if (!a) return;
      var key = a.getAttribute('data-switch-to');
      var pane = document.querySelector('[data-switch-panel="' + key + '"]');
      var box = pane && pane.closest('[data-eb-switcher]');
      var btn = box && box.querySelector('[data-switch="' + key + '"]');
      if (btn) btn.click();
    });
  }

  /* ══ WHAT CHANGED · an accordion that turns itself ═════════════════════
     The band is a record — you said it, we did something, here is the date —
     and a record read as a wall of rows is read as nothing. So one row is
     open at a time and it advances on its own, slowly, with the panel fading
     rather than sliding: the eye is drawn back to it once, not dragged.

     THE QUOTE IS DRAWN AT RANDOM from the reviews that said this thing, so
     the band is not three sentences somebody chose — it is three subjects,
     and a different rider says each one every time you look. The pool comes
     from the markup (data-quotes, a JSON array), so with no script at all the
     first quote of each row is printed and the band is a plain list.

     It stops the moment anybody touches it: hovering, focusing, or opening a
     row by hand ends the rotation for good, because a thing that moves while
     you are reading it is a thing you cannot read. Reduced motion and the
     merchant's data-motion="off" switch both mean: no rotation at all. */
  function said(box) {
    var rows = slice(box.querySelectorAll('[data-eb-said-row]'));
    if (rows.length < 2) return;
    var every = (parseInt(box.getAttribute('data-every'), 10) || 7) * 1000;
    var still = reduce.matches || root.getAttribute('data-motion') === 'off';
    var i = rows.findIndex(function (r) { return r.classList.contains('is-on'); });
    if (i < 0) i = 0;
    var timer = null, stopped = false;

    function quoteOf(row) {
      var q = row.querySelector('[data-eb-said-quote]');
      if (!q) return;
      var pool;
      try { pool = JSON.parse(q.getAttribute('data-quotes') || '[]'); } catch (e) { pool = []; }
      if (pool.length < 2) return;
      var next = pool[Math.floor(Math.random() * pool.length)];
      if (next === q.textContent) next = pool[(pool.indexOf(next) + 1) % pool.length];
      q.textContent = next;
    }

    function show(n, rotate) {
      i = (n + rows.length) % rows.length;
      rows.forEach(function (r, k) {
        var on = k === i;
        r.classList.toggle('is-on', on);
        var t = r.querySelector('[data-eb-said-trigger]');
        if (t) t.setAttribute('aria-expanded', on ? 'true' : 'false');
      });
      if (rotate) quoteOf(rows[i]);
    }

    function stop() {
      stopped = true;
      if (timer) { clearInterval(timer); timer = null; }
      box.classList.add('is-held');
    }

    show(i, false);
    if (!still) {
      timer = setInterval(function () { if (!stopped) show(i + 1, true); }, every);
      box.addEventListener('pointerenter', stop);
      box.addEventListener('focusin', stop);
    }
    box.addEventListener('click', function (e) {
      var t = e.target.closest('[data-eb-said-trigger]');
      if (!t) return;
      stop();
      show(rows.indexOf(t.closest('[data-eb-said-row]')), false);
    });
  }

  function switcher(box) {
    if (box.hasAttribute('data-eb-tabs')) buildTabs(box);
    var btns = slice(box.querySelectorAll('[data-switch]'));
    var panes = slice(box.querySelectorAll('[data-switch-panel]'));
    if (btns.length < 2 || !panes.length) return;

    function show(v) {
      btns.forEach(function (b) {
        b.setAttribute('aria-selected', b.getAttribute('data-switch') === v ? 'true' : 'false');
      });
      panes.forEach(function (p) {
        p.hidden = p.getAttribute('data-switch-panel') !== v;
      });
    }
    /* THE MARKUP DECIDES, and this only heals it. Whichever panel arrives
       without `hidden` is the open one — that is how the sections have always
       written it, and it is what holds before this script runs. If the markup
       says nothing, or says two things, the first panel wins rather than
       leaving two forms stacked on each other. */
    var opener = panes.filter(function (p) { return p.hasAttribute('data-start-open'); });
    var shown = panes.filter(function (p) { return !p.hidden; });
    var first = opener[0] || (shown.length === 1 ? shown[0] : panes[0]);
    show(first.getAttribute('data-switch-panel'));

    /* 选车按钮排不下、横向滑的时候：哪一侧还有内容，就给 tablist 写 is-more-start / is-more-end，
       CSS 用渐隐画出来（只在真溢出时出现，不靠数按钮个数）。点到半露的按钮时把它滑进来——
       只滚这一排，不用 scrollIntoView，否则整页也会跟着跳。 */
    var list = btns[0].parentElement;
    function edges() {
      var max = list.scrollWidth - list.clientWidth;
      list.classList.toggle('is-more-start', max > 1 && list.scrollLeft > 1);
      list.classList.toggle('is-more-end', max > 1 && list.scrollLeft < max - 1);
    }
    list.addEventListener('scroll', edges, { passive: true });
    window.addEventListener('resize', edges);
    edges();

    box.addEventListener('click', function (e) {
      var b = e.target.closest('[data-switch]');
      if (!b || !box.contains(b)) return;
      show(b.getAttribute('data-switch'));
      if (list.scrollWidth > list.clientWidth + 1) {
        var l = b.getBoundingClientRect().left - list.getBoundingClientRect().left + list.scrollLeft;
        var r = l + b.offsetWidth;
        var to = l < list.scrollLeft + 24 ? l - 24
               : r > list.scrollLeft + list.clientWidth - 24 ? r - list.clientWidth + 24 : null;
        if (to !== null) list.scrollTo({ left: to, behavior: motionOff() ? 'auto' : 'smooth' });
      }
    });
    /* The keyboard walks a tablist with the arrow keys or it is not a
       tablist, whatever the roles say. */
    box.addEventListener('keydown', function (e) {
      var b = e.target.closest('[data-switch]');
      if (!b) return;
      var i = btns.indexOf(b), n = btns.length, j = -1;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') j = (i + 1) % n;
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') j = (i - 1 + n) % n;
      if (e.key === 'Home') j = 0;
      if (e.key === 'End') j = n - 1;
      if (j < 0) return;
      e.preventDefault();
      btns[j].focus();
      show(btns[j].getAttribute('data-switch'));
    });
  }

  /* ══ PICKERS · one open at a time ══════════════════════════════════════
     <details class="eb-picker"> is the theme's own select, and until now the
     only form that used one had a single picker on it. Two side by side — a
     day and a month — and both stayed open at once, two panels overlapping
     with two scrollbars, because nothing was closing the other.

     So: opening one closes every other picker on the page, a click outside
     closes them all, and Escape closes the one you are in. Delegated on the
     document, because the pickers are written by four different snippets and
     none of them should have to know about this. */
  function bindPickers() {
    if (document.documentElement.dataset.ebPickersBound) return;
    document.documentElement.dataset.ebPickersBound = '1';

    function closeAll(except) {
      slice(document.querySelectorAll('details[data-eb-picker][open]')).forEach(function (d) {
        if (d !== except) d.open = false;
      });
    }
    /* WHICH WAY IT OPENS. A panel is an absolutely-positioned child, so
       inside a dialog it is CLIPPED by the body's own scroll box rather than
       merely hanging over the page — a picker near the bottom of a short
       dialog lost its last rows. The region picker in the header already has
       the answer and a class for it, so this is the same move: if the space
       below the trigger cannot hold the panel and there is more above, it
       opens upward. Measured on open, because the dialog's height depends on
       the viewport and the form scrolls. */
    function place(d) {
      var pop = d.querySelector('.eb-popover');
      var trigger = d.querySelector('summary');
      if (!pop || !trigger) return;
      d.classList.remove('is-up');
      var box = d.closest('.eb-dialog__body, .eb-dialog, .eb-sheet, .eb-drawer');
      var edge = box ? box.getBoundingClientRect() : { top: 0, bottom: window.innerHeight };
      var t = trigger.getBoundingClientRect();
      var need = pop.offsetHeight + 8;
      var below = edge.bottom - t.bottom;
      var above = t.top - edge.top;
      if (below < need && above > below) d.classList.add('is-up');
    }

    /* `toggle` does not bubble, so it is captured. */
    document.addEventListener('toggle', function (e) {
      var d = e.target;
      if (!d.matches || !d.matches('details[data-eb-picker]')) return;
      if (d.open) { closeAll(d); place(d); }
      else d.classList.remove('is-up');
    }, true);

    document.addEventListener('click', function (e) {
      if (!e.target.closest('details[data-eb-picker]')) closeAll(null);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      var open = document.querySelector('details[data-eb-picker][open]');
      if (!open) return;
      /* Stop here: a dialog behind the picker must not also close. */
      e.stopPropagation();
      open.open = false;
      var trigger = open.querySelector('summary');
      if (trigger) trigger.focus();
    }, true);
  }

  /* ══ MORE BELOW ════════════════════════════════════════════════════════
     Only ever needed on a phone: above the breakpoint the card is sized so
     nothing is cut, and the CSS keeps this hidden there. The button scrolls
     the form on and the pair goes the moment the end is reached — an arrow
     pointing at nothing is worse than no arrow.

     It watches rather than measuring once, because the form changes height
     under it: a month is typed and a figure lights, a form comes back with
     an error. */
  function more(btn) {
    var dialog = btn.closest('.eb-dialog');
    var body = dialog && dialog.querySelector('.eb-dialog__body');
    var wrap = btn.parentNode;
    if (!dialog || !body) return;

    function sync() {
      var left = body.scrollHeight - body.clientHeight - body.scrollTop;
      wrap.hidden = !(left > 8);
    }
    btn.addEventListener('click', function () {
      body.scrollBy({ top: Math.round(body.clientHeight * 0.72),
                      behavior: motionOff() ? 'auto' : 'smooth' });
      /* A smooth scroll's last frame does not always land a scroll event at
         the resting position, so ask once more when it must have stopped. */
      window.setTimeout(sync, 700);
    });
    body.addEventListener('scroll', sync, { passive: true });
    body.addEventListener('scrollend', sync);
    window.addEventListener('resize', sync);
    /* A dialog is display:none until it opens, so at bind time everything
       measures zero. The opening is the moment that matters and it arrives by
       several routes, so the element itself is watched. */
    if (window.ResizeObserver) {
      var ro = new ResizeObserver(sync);
      ro.observe(body);
      ro.observe(dialog);
      if (body.firstElementChild) ro.observe(body.firstElementChild);
    }
    dialog.addEventListener('toggle', function () {
      if (dialog.open) body.scrollTop = 0;
      sync();
    });
    requestAnimationFrame(sync);
    sync();
  }

  /* ── bind ────────────────────────────────────────────────────────────── */
  /* ══ THE CHARGE CLOCK ══════════════════════════════════════════════════
     Six to eight hours is a figure everybody prints and nobody pictures. The
     question a buyer actually has is "plug it in when I get home — can I ride
     before work", and that is ARITHMETIC on the product's own two numbers
     rather than a claim: start + 6 and start + 8, wrapped at midnight.

     It writes two custom properties and one line of text; the ring, the ticks
     and the arc are all CSS. Nothing here decides how long the bike takes to
     charge — the section states that, and this only adds it up. */
  function clock(box) {
    var input = box.querySelector('[data-eb-clock-input]');
    var read = box.querySelector('[data-eb-clock-read]');
    var lo = parseFloat(box.dataset.ebClockMin || 6);
    var hi = parseFloat(box.dataset.ebClockMax || 8);
    if (!input) return;
    function two(n) { return (n < 10 ? '0' : '') + n; }
    function paint() {
      var start = parseInt(input.value, 10) || 0;
      /* The arc starts at the top of the dial, which is midnight, and a turn
         is 24 hours — so an hour is 1/24 of a turn and no degrees appear
         anywhere in this file. */
      box.style.setProperty('--from', (start / 24).toFixed(4));
      box.style.setProperty('--span', (hi / 24).toFixed(4));
      if (read) {
        read.textContent = two(Math.round((start + lo) % 24)) + ':00 – '
          + two(Math.round((start + hi) % 24)) + ':00';
      }
      var at = box.querySelector('[data-eb-clock-at]');
      if (at) at.textContent = two(start) + ':00';
    }
    input.addEventListener('input', paint);
    paint();
  }

  /* ══ STANCE ════════════════════════════════════════════════════════════
     A rider height in, and the bike or BIKES it lands on out. The bands come
     from the markup rather than from here, so a fourth frame is a fourth
     data attribute and not a code change.

     AT AN OVERLAP IT NAMES BOTH. 178 and 190 are edges shared by two frames,
     and the shop's rule is that neither is quietly chosen for the reader — so
     the test is inclusive at both ends and the answer is allowed to be two. */
  function stance(box) {
    var input = box.querySelector('[data-eb-stance-input]');
    var out = box.querySelector('[data-eb-stance-out]');
    var note = box.querySelector('[data-eb-stance-note]');
    var towers = slice(box.querySelectorAll('[data-band]'));
    if (!input || !towers.length) return;
    function paint() {
      var h = parseInt(input.value, 10);
      var hit = [];
      towers.forEach(function (t) {
        var b = t.dataset.band.split('-');
        var on = h >= +b[0] && h <= +b[1];
        t.classList.toggle('is-on', on);
        if (on) hit.push(t.dataset.name);
      });
      var at = box.querySelector('[data-eb-stance-at]');
      if (at) at.textContent = h + ' cm';
      if (out) out.textContent = hit.length ? hit.join(' or ') : 'Outside every band';
      if (note) {
        note.textContent = hit.length === 0
          ? 'Ask us rather than take a guess from this page — outside the bands is not a size we ship.'
          : (hit.length > 1
            ? 'You are on an edge, so both are named. The deciding question is how you want to get on, not which is bigger.'
            : note.dataset.one || '');
      }
    }
    input.addEventListener('input', paint);
    paint();
  }

  /* ══ LOAD ══════════════════════════════════════════════════════════════
     One number comes from the shop — the rated total — and the rest is the
     visitor's own arithmetic. A calculator that invents what a pannier weighs
     is a calculator making claims on the shop's behalf. */
  function load(box) {
    var input = box.querySelector('[data-eb-load-input]');
    var left = box.querySelector('[data-eb-load-left]');
    var at = box.querySelector('[data-eb-load-at]');
    var max = parseFloat(box.dataset.ebLoadMax || 200);
    var bike = parseFloat(box.dataset.ebLoadBike || 0);
    if (!input) return;
    function paint() {
      var carried = parseInt(input.value, 10) || 0;
      var total = carried + bike;
      var pc = Math.min(100, (total / max) * 100);
      box.style.setProperty('--pc', pc.toFixed(1));
      box.classList.toggle('is-over', total > max);
      if (at) at.textContent = carried + ' kg';
      if (left) {
        /* The bike's own weight is a decimal, so the subtraction is one too —
           and 200 - 43.4 - 120 in binary floating point is
           36.599999999999994, which is not a number anybody should read on a
           product page. One decimal, and a whole number stays whole. */
        var n = total > max ? total - max : max - total;
        n = Math.round(n * 10) / 10;
        left.textContent = n + (total > max ? ' kg over' : ' kg left');
      }
    }
    input.addEventListener('input', paint);
    paint();
  }

  function bind(sel, fn, scope) {
    slice((scope || document).querySelectorAll(sel)).forEach(function (el) {
      if (el.dataset.ebBound) return;
      el.dataset.ebBound = '1';
      fn(el);
    });
  }
  /* ══ THE STANDALONE COUNTDOWN ══════════════════════════════════════════
     The announcement bar has always had one, and it is bar-specific: it also
     removes the slot when the clock runs out. The CAMPAIGN band prints the same
     `.eb-count` with a `data-until` and its comment says the script fills it —
     and nothing ever did. The bar's filler is keyed on `[data-eb-countdown]`
     and scoped to the bar, so every campaign banner since v4.0 has shipped an
     EMPTY counter beside its end date. Measured 2026-09-10.

     THE DIGITS ARE BUILT HERE, not in Liquid. A number rendered server-side is
     wrong the moment the page is cached, and this one is wrong by exactly as
     long as the cache is old.

     AND IT TELLS THE PAGE WHEN IT IS OVER: `.is-over` goes on the nearest
     [data-eb-campaign], so a band can show what it says after the end instead
     of counting down to nothing. A campaign that ends at 23:59 and still reads
     "00 d 00 h" at ten past midnight is a shop lying about its own offer. */
  function countdown(el) {
    var end = Date.parse(el.getAttribute('data-until'));
    if (isNaN(end)) return;
    var band = el.closest('[data-eb-campaign]');
    var units = [['d', el.getAttribute('data-t-d') || 'd'],
                 ['h', el.getAttribute('data-t-h') || 'h'],
                 ['m', el.getAttribute('data-t-m') || 'm']];
    var cells = {};
    units.forEach(function (u) {
      var n = document.createElement('span');
      var l = document.createElement('i');
      l.textContent = u[1];
      el.appendChild(n); el.appendChild(l);
      cells[u[0]] = n;
    });
    var pad = function (n) { return String(n).padStart(2, '0'); };
    function tick() {
      var s = Math.floor((end - Date.now()) / 1000);
      if (s <= 0) {
        window.clearInterval(t);
        el.hidden = true;
        if (band) band.classList.add('is-over');
        return;
      }
      cells.d.textContent = pad(Math.floor(s / 86400));
      cells.h.textContent = pad(Math.floor(s % 86400 / 3600));
      cells.m.textContent = pad(Math.floor(s % 3600 / 60));
      /* Under a day it is urgent, and the colour is the warning token — never
         a red typed into a section. */
      el.classList.toggle('is-urgent', s < 86400);
    }
    var t = window.setInterval(tick, 30000);
    tick();
  }

  /* 时间线的「现在」由时钟决定：每一格都填了日期时，当前格 = 下一个还没发生的那一格，
     已经过去的变淡；全都过去了就停在最后一格。只要有一格没填日期就不接管，
     仍按后台那个勾选框来——一半自动一半手动的时间线，比全手动更容易出错。
     不在 Liquid 里判断：页面缓存多久，服务端算的「现在」就错多久。 */
  function chrono(el) {
    var stops = el.querySelectorAll('.eb-chrono__stop');
    if (!stops.length) return;
    var at = Array.prototype.map.call(stops, function (s) { return Date.parse(s.getAttribute('data-at')); });
    if (at.some(isNaN)) return;
    function sync() {
      var now = Date.now(), cur = at.length - 1;
      for (var i = 0; i < at.length; i++) { if (at[i] > now) { cur = i; break; } }
      stops.forEach(function (s, i) {
        s.classList.toggle('is-now', i === cur);
        s.classList.toggle('is-past', i < cur);
      });
    }
    sync();
    window.setInterval(sync, 60000);
  }

  function init(scope) {
    bind('[data-eb-chrono]', chrono, scope);
    bind('[data-eb-panels]', panels, scope);
    bind('[data-eb-quotes]', quotes, scope);
    bind('[data-eb-rail]', rail, scope);
    bind('[data-eb-said]', said, scope);
    bind('[data-eb-accordion]', accordion, scope);
    bind('[data-eb-count][data-until]', countdown, scope);
    bind('[data-eb-facade]', facade, scope);
    bind('[data-eb-clock]', clock, scope);
    bind('[data-eb-stance]', stance, scope);
    bind('[data-eb-load]', load, scope);
    bind('[data-eb-contact]', contactForm, scope);
    bind('[data-eb-switcher]', switcher, scope);
    bind('[data-eb-more]', more, scope);
    bindSwitchLinks();
    bindPickers();
  }
  /* In the editor, selecting an Accordion block opens it — a closed panel is
     a block the merchant cannot see while editing it. */
  document.addEventListener('shopify:block:select', function (e) {
    var item = e.target && e.target.closest ? e.target.closest('.eb-blk-acc') : null;
    if (!item) return;
    item.classList.add('is-open');
    var t = item.querySelector('.eb-accordion__trigger');
    if (t) t.setAttribute('aria-expanded', 'true');
  });

  window.ebComponents = { init: init };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(); });
  else init();
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
